// src/components/DepartmentModal.js
import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axiosInstance";

function DepartmentModal({
  isModalOpen,
  setIsModalOpen,
  handleCloseModal,
  handleFormSubmit,
  formData,
  handleInputChange,
  title, // Dynamic title for the modal
  submitButtonText, // Dynamic text for the submit button
}) {
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const userRole = userDetails.role;

  useEffect(() => {
    const fetchBranches = async () => {
      if (userRole === "hospital_admin" && isModalOpen) {
        try {
          const response = await axiosInstance.get("/hospitals/branches");
          const branchesData = response.data.branches || response.data || [];
          setBranches(branchesData);

          // Auto-select if only one branch
          if (branchesData.length === 1) {
            setSelectedBranches([branchesData[0].id]);
          }
        } catch (error) {
          console.error("Error fetching branches:", error);
        }
      }
    };
    fetchBranches();
  }, [userRole, isModalOpen]);

  const handleBranchToggle = (branchId) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  const getSelectedBranchNames = () => {
    if (selectedBranches.length === 0) return "Select branches";
    if (selectedBranches.length === branches.length)
      return "All branches selected";
    return `${selectedBranches.length} branch(es) selected`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmitWithBranches = (e) => {
    e.preventDefault();

    // Validation for hospital_admin
    if (userRole === "hospital_admin" && selectedBranches.length === 0) {
      alert("Please select at least one branch");
      return;
    }

    // Pass selected branches to parent
    handleFormSubmit(e, selectedBranches);

    // Reset selected branches
    setSelectedBranches([]);
  };

  if (!isModalOpen) return null; // Don't render anything if the modal is not open

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <form onSubmit={handleSubmitWithBranches}>
          <div className="mb-4">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-600"
            >
              Department Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-600"
            >
              Department Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="mt-1 p-2 w-full border rounded-md"
              rows="3"
              required
            ></textarea>
          </div>

          {/* Branch Selection for hospital_admin */}
          {userRole === "hospital_admin" && (
            <div className="mb-4" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {branches.length === 1 ? "Branch" : "Select Branches *"}
              </label>

              {/* Single branch - show as text/readonly */}
              {branches.length === 1 ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                  {branches[0].name}
                </div>
              ) : (
                /* Multiple branches - show dropdown */
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex items-center justify-between"
                  >
                    <span
                      className={
                        selectedBranches.length === 0
                          ? "text-gray-400"
                          : "text-gray-700"
                      }
                    >
                      {getSelectedBranchNames()}
                    </span>
                    <svg
                      className={`w-5 h-5 transition-transform ${
                        isDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {branches.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No branches available
                        </div>
                      ) : (
                        <>
                          <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b border-gray-200">
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  selectedBranches.length === branches.length
                                ) {
                                  setSelectedBranches([]);
                                } else {
                                  setSelectedBranches(
                                    branches.map((b) => b.id)
                                  );
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {selectedBranches.length === branches.length
                                ? "Deselect All"
                                : "Select All"}
                            </button>
                          </div>
                          {branches.map((branch) => (
                            <label
                              key={branch.id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedBranches.includes(branch.id)}
                                onChange={() => handleBranchToggle(branch.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {branch.name}
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleCloseModal}
              className="mr-2 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200"
            >
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DepartmentModal;
